/**
 * The title (docs/spec/07 §2, 04 §7): the STONEFALL logo tumbling into place over raster bars, a
 * sinking field of cave tiles, the menu, and a sine scroller. The first visit of a page load asks
 * for a key first (which unlocks audio); twenty idle seconds start the attract demo.
 */

import { GAME_NAME, HERO_NAME } from '../../app/brand';
import { BLACK, WHITE } from '../../render/palette';
import type { Screen } from '../../render/screen';
import type { MenuAction, PointerInput, SceneHost } from '../scene';
import { TileField } from '../kit/backdrop';
import { UiScene } from '../kit/base';
import { drawPanel, drawReveal } from '../kit/chrome';
import type { LogoArt } from '../kit/logo';
import { drawLogo, dropDone, logoFor } from '../kit/logo';
import { Marquee } from '../kit/marquee';
import { drawMenu } from '../kit/menu';
import type { ListLayout } from '../kit/menuModel';
import { moveSelection, rowAt } from '../kit/menuModel';
import type { RasterBar } from '../kit/raster';
import { drawRasterBars, drawRasterRule } from '../kit/raster';
import { commas } from '../kit/text';
import { ACCENT, cycle, H, MUTED, RAMPS, setCalm, W } from '../kit/theme';
import { IDLE_MS, marqueeText, TITLE_ITEMS } from './titleMenu';

/** The first key of this page load has been pressed. */
let pressed = false;
/** The menu remembers its place for the session. */
let lastSel = 0;

const BAND_H = 64;
const LOGO_Y = 11;
const FIELD_BOTTOM = 178;
const MENU: ListLayout & { cx: number } = { top: 72, rowH: 10, x: 72, w: 176, cx: W / 2 };
const PANEL = { x: 72, y: 67, w: 176, h: 106 };
const PRESS_PANEL = { x: 64, y: 90, w: 192, h: 62 };

const BARS: readonly RasterBar[] = [
  { ramp: RAMPS.blue, speed: 0.0021, phase: 0 },
  { ramp: RAMPS.purple, speed: 0.0021, phase: 1.6 },
  { ramp: RAMPS.fire, speed: 0.0021, phase: 3.2 },
  { ramp: RAMPS.green, speed: 0.0021, phase: 4.8 },
];

export class TitleScene extends UiScene {
  protected readonly heading = '';
  protected override readonly track = null;
  private sel = lastSel;
  private waiting: boolean;
  private idle = 0;
  private dropMs: number | null = 0;
  private readonly logo: LogoArt;
  private readonly field = new TileField();
  private readonly marquee: Marquee;

  constructor(host: SceneHost, skipPress: boolean) {
    super(host);
    this.waiting = !pressed && !skipPress;
    this.logo = logoFor(GAME_NAME);
    const top = host.services.scores.table('classic')[0];
    const best = top ? `${commas(top.score)} BY ${top.initials.trim()}` : '0';
    this.marquee = new Marquee(marqueeText(GAME_NAME, HERO_NAME, best));
  }

  override enter(): void {
    super.enter();
    this.idle = 0;
    if (!this.waiting) this.services.audio.music('title');
  }

  protected override describe(): string {
    if (this.waiting) return `${GAME_NAME}. Press any key to start.`;
    return `${GAME_NAME}. Main menu. ${TITLE_ITEMS[this.sel]?.label ?? ''}`;
  }

  protected override tick(dt: number): void {
    this.idle += dt;
    this.marquee.update(dt);
    if (this.dropMs !== null) {
      this.dropMs += dt;
      if (dropDone(this.dropMs, this.logo.letters.length)) this.dropMs = null;
    }
    if (this.idle >= IDLE_MS) this.host.go({ to: 'play', request: { mode: 'demo' } });
  }

  override draw(screen: Screen): void {
    setCalm(this.services.options.get().reduceFlashing);
    screen.clear(BLACK);
    this.paint(screen);
    drawReveal(screen, this.ms);
  }

  protected paint(screen: Screen): void {
    const ms = this.ms + 100_000;
    drawRasterBars(screen, 0, BAND_H, ms, BARS, 1);
    this.field.draw(screen, ms, BAND_H, FIELD_BOTTOM);
    drawRasterRule(screen, BAND_H, ms, RAMPS.grey);
    drawLogo(screen, this.logo, Math.floor((W - this.logo.w) / 2), LOGO_Y, this.ms, this.dropMs);
    if (this.waiting) this.paintPress(screen);
    else {
      drawPanel(screen, PANEL);
      drawMenu(screen, TITLE_ITEMS, this.sel, { ...MENU, slide: true, bar: true }, this.ms);
    }
    screen.fill(0, FIELD_BOTTOM, W, H - FIELD_BOTTOM, BLACK);
    drawRasterRule(screen, FIELD_BOTTOM, ms, RAMPS.blue);
    this.marquee.draw(screen, 186, this.ms);
  }

  private paintPress(screen: Screen): void {
    drawPanel(screen, PRESS_PANEL);
    const text = 'PRESS ANY KEY';
    const x0 = W / 2 - (text.length * 8) / 2;
    [...text].forEach((ch, i) => screen.text(ch, x0 + i * 8, 102, cycle(RAMPS.fire, this.ms, 70, -i)));
    screen.text('OR CLICK, OR TAP', W / 2, 116, MUTED, 'center');
    const top = this.services.scores.table('classic')[0];
    if (top) {
      screen.text('HIGH SCORE', W / 2 - 4, 136, ACCENT, 'right');
      screen.text(`${commas(top.score)} ${top.initials}`, W / 2 + 4, 136, WHITE);
    }
  }

  private press(): void {
    pressed = true;
    this.waiting = false;
    this.idle = 0;
    this.services.audio.unlock();
    this.services.audio.music('title');
    this.sound('select');
    this.say(this.describe());
  }

  private move(delta: number): void {
    const next = moveSelection(TITLE_ITEMS, this.sel, delta);
    if (next === this.sel) return;
    this.select(next);
  }

  private select(i: number): void {
    this.sel = i;
    lastSel = i;
    this.sound('move');
    this.say(TITLE_ITEMS[i]?.label ?? '');
  }

  private choose(i: number): void {
    const item = TITLE_ITEMS[i];
    if (!item) return;
    lastSel = i;
    this.sound('select');
    if (item.replace === true) this.host.go(item.route);
    else this.host.push(item.route);
  }

  keydown(): boolean {
    this.idle = 0;
    if (!this.waiting) return false;
    this.press();
    return true;
  }

  protected onAction(a: MenuAction): void {
    this.idle = 0;
    if (this.waiting) return this.press();
    if (a === 'up' || a === 'left') this.move(-1);
    else if (a === 'down' || a === 'right') this.move(1);
    else if (a === 'confirm') this.choose(this.sel);
  }

  /** Esc on the title has nowhere to go. */
  protected override back(): void {
    this.idle = 0;
    if (this.waiting) this.press();
  }

  protected override onPointer(p: PointerInput): void {
    this.idle = 0;
    if (this.waiting) {
      if (p.kind === 'down') this.press();
      return;
    }
    if (p.kind === 'wheel') return this.move(p.delta);
    const i = rowAt(MENU, TITLE_ITEMS.length, p.x, p.y);
    if (i < 0) return;
    if (p.kind === 'move' && i !== this.sel) this.select(i);
    else if (p.kind === 'down' && p.button === 0) this.choose(i);
  }
}
